import { TextField, Autocomplete } from "@mui/material";
import {
  useState,
  useEffect,
  useContext,
  type SyntheticEvent,
} from "react";
import { useLocation } from "react-router-dom";
import { getCustomers } from "../api/customers";
import { TokenContext } from "../TokenContext";
import { IsAgent } from "../role";
import type { CustomerItem, CustomerUserEntry } from "../types/site";

type CustomerSelectProps = {
  prefCode: string | string[] | null;
  branchId: number | null;
  changedValue: (value: number[]) => void;
};

export default function CustomerSelect({
  prefCode,
  branchId,
  changedValue,
}: CustomerSelectProps) {
  const tokenContext = useContext(TokenContext);
  const user = tokenContext?.user ?? null;
  const isAgent = user ? IsAgent(user.m_organization.m_role_id) : false;
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");
  const [customers, setCustomers] = useState<CustomerItem[]>([]);
  const [selectedOption, setSelectedOption] = useState<CustomerItem[]>([]);
  const [isDisabled, setDisabled] = useState(false);

  const fetchCustomers = async () => {
    try {
      const response = await getCustomers({
        disaster_id: disasterId,
        pref_code: prefCode,
        m_branch_id: branchId,
        order: null,
        sort: null,
        page: 1,
      });
      if (response.status !== "ok") {
        return;
      }
      const data = response.m_customers;
      setCustomers(Array.isArray(data) ? data : []);
      if (isAgent && user) {
        const customerUsers =
          (user.customer_users as CustomerUserEntry[] | undefined) ?? [];
        const ownCustomers = customerUsers.map(
          (entry) => entry.m_customer
        );
        const existData = ownCustomers.filter((target) =>
          data.some((option) => option.id === target.id)
        );
        setSelectedOption(existData);
        if (existData.length > 0) {
          changedValue(existData.map((value) => value.id));
        }
        setDisabled(true);
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disasterId, prefCode, branchId]);

  const handleChange = (
    _event: SyntheticEvent,
    newValues: CustomerItem[]
  ) => {
    setSelectedOption(newValues);
    changedValue(newValues.map((newValue) => newValue.id));
  };

  return (
    <Autocomplete
      multiple
      limitTags={2}
      fullWidth
      size="small"
      options={customers}
      getOptionLabel={(customer) => customer.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField {...params} placeholder="複数選択可" />
      )}
      disabled={isDisabled}
      value={selectedOption}
      onChange={handleChange}
    />
  );
}
